import React, { useEffect, useMemo, useState } from 'react';
import { Money, SeatmapSelect } from '@arema/components';
import { Header, Button, Modal, Table, useFrontier } from 'react-frontier2';
import { EventDate, EventDateData, SetLoading } from '@arema/components/Classes';
import { SeatmapSeat } from '@arema/components/Classes';
import { addCommas, bindClick, fromUnix } from '@arema/components/Util';
import { useAPI } from '@arema/components/Hooks';
import { useTranslation } from 'react-i18next';
import SelectDateModal from './SelectDateModal';
import API from '../API';

import '@arema/components/style/seatmap.scss';

interface UpdateSeatTicketProps {
	seatId?: number,
	eventId: number,
	dateId: number,
	sectionId: number,
	title?: string,
	open: boolean,
	onClose: ()=>void,
	onSave?: (seat: SeatmapSeat, date: EventDateData, section: EventDateData["sections"][0], price: EventDateData["sections"][0]["prices"][0], l: SetLoading)=>void,
}

enum SelectModal{
	DATE = 1,
	SECTION = 2,
	PRICE = 3,
}

function UpdateSeatTicket(props: UpdateSeatTicketProps){
	var { t } = useTranslation();
	var [selectModal, setSelectModal] = useState<SelectModal>(null);
	var { Toast } = useFrontier();
	var { data: seatmap, load: loadSeatmap, loadError } = useAPI(API.getDateSeatmap);
	var { data: selectedDate, load: loadDate, loadError: dateLoadError } = useAPI(API.getDate);
	var [selectedSeat, setSelectedSeat] = useState<SeatmapSeat>(null);
	var [selectedSection, setSelectedSection] = useState<EventDateData["sections"][0]>(null);
	var [selectedPrice, setSelectedPrice] = useState<typeof selectedSection["prices"][0]>(null);

	useEffect(()=>{
		if(!props.open) return;
		loadSeatmap(props.dateId, props.sectionId);
	}, [props.open, props.sectionId, props.dateId]);

	const onSeatClick = (seat: SeatmapSeat[])=>{
		setSelectedSeat(seat[seat.length-1] || null);
	}

	var showSelectDate = ()=>{
		setSelectModal(SelectModal.DATE);
		setSelectedSection(null);
		setSelectedPrice(null);
		setSelectedSeat(null);
	}

	var onDateSelected = (d: EventDate[])=>{
		setSelectModal(SelectModal.SECTION);
		setSelectedPrice(null);
		setSelectedSection(null);
		setSelectedSeat(null);
		loadDate(d[0].date_id, ['sections', 'prices'], ['delivery', 'payment_methods', 'venue']);
	}

	var onPriceSelected = (p: EventDateData["sections"][0]["prices"][0])=>{
		return ()=>{
			setSelectedPrice(p);
			loadSeatmap(selectedDate.date_id, selectedSection.section_id);
			setSelectModal(null);
		}
	}
	
	var onSectionSelected = (s: EventDateData["sections"][0])=>{
		return ()=>{
			if(s.prices.length===0) return Toast.error('La sección seleccionada no tiene precios activados');
			setSelectedSection(s);
			if(s.prices.length===1){
				setSelectedPrice(s.prices[0]);
				loadSeatmap(selectedDate.date_id, s.section_id);
				setSelectModal(null);
			}
		}
	}

	var sendSave = (setLoading: SetLoading)=>{
		if(props.onSave) props.onSave(selectedSeat, selectedDate, selectedSection, selectedPrice, setLoading);
	}

	var modified_seatmap = useMemo(()=>{
		if(!seatmap) return null;
		var stm = [...seatmap.seats];
		if(props.seatId){
			var ix = stm.findIndex(a=>a.seat_id===props.seatId);
			if(ix!==-1) stm[ix].special = true;
		}
		return {
			...seatmap,
			seats: stm,
		};
	}, [seatmap]);

	return <>
		<Modal open={props.open} onClose={props.onClose} size='large'>
			<Modal.Header actions={(
				<Button text='Cambiar' color='blue' menu={[
					{ text: 'Cambiar fecha', onClick: showSelectDate }
				]} />
			)}>{props.title || 'Asignar Butaca'}</Modal.Header>
			<Modal.Content style={{ padding: 0 }}>
				{(!seatmap || loadError) ? (
					<div style={{ height: 600, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
						<Header loading={!loadError} text={loadError || 'Cargando mapa...'} iconName={loadError ? 'face-frown-open' : null} size={loadError ? 'small' : null} />
					</div>
				) : (
					<SeatmapSelect
						seatmap={modified_seatmap} 
						error={loadError} 
						onSelected={onSeatClick} 
						loading={!seatmap} 
						selectSold={false}
						selectBlocked={false}
						selected={selectedSeat?.seat_id ? [selectedSeat.seat_id] : []} 
						seatStyle={{
							width: 30,
							height: 30,
							fontSize: 10,
							borderRadius: 6,
							text: false,
						}}
					/>
				)}
			</Modal.Content>
			<Modal.Actions>
				<Button basic text='Cerrar' onClick={props.onClose} />
				{props.onSave && (
					<Button color="black" text='Guardar' onClick={sendSave} disabled={!selectedSeat} />
				)}
			</Modal.Actions>
		</Modal>
		<SelectDateModal
			open={selectModal===SelectModal.DATE}
			allEvent={false}
			multiple={false}
			eventId={props.eventId}
			onClose={bindClick(setSelectModal)}
			onSelected={onDateSelected}
		/>
		<Modal open={selectModal===SelectModal.SECTION} size='tiny' onClose={bindClick(setSelectModal)}>
			<Modal.Header text='Selecciona la sección' />
			<Modal.Content>
				{selectedDate ? <>
					<Table details fitted striped style={{ marginTop: -20 }}>
						<Table.Body>
							<Table.Row data={['Calendario', selectedDate.date_id]} />
							<Table.Row data={['Fecha', fromUnix(selectedDate.date).toFormat('dd/MMM/yy HH:mm')]} />
							<Table.Row data={['Recinto', selectedDate.venue_name]} />
							{!!selectedSection && (
								<Table.Row data={['Sección', selectedSection.section_name]} />
							)}
						</Table.Body>
					</Table>
					{!selectedSection ? (
						<Table fitted selectable style={{ marginBottom: -20 }}>
							<Table.Head title='Secciones' data={[['ID', 'Sección', 'Precio']]} />
							<Table.Body>
								{selectedDate?.sections.filter(a=>!!a.numbered).sort((a,b)=>a.sort_order-b.sort_order).map(a=>(
									<Table.Row
										key={`SCT-${a.section_id}`}
										collapsingIndexes={[0, 2]}
										centeredIndexes={[2]}
										onClick={onSectionSelected(a)}
										data={[
											a.section_id, 
											a.section_name, 
											<>
												{addCommas(a.prices_min, true, true)}
												{a.prices_min<a.prices_max ? ` - ${addCommas(a.prices_max)}` : null}
											</>
										]} 
									/>
								))}
							</Table.Body>
						</Table>
					) : (
						<Table fitted selectable style={{ marginBottom: -20 }}>
							<Table.Head title='Precios' data={[['ID', 'Nombre', 'Precio']]} />
							<Table.Body>
								{selectedSection.prices.map(a=>(
									<Table.Row
										key={`SCTPR-${a.price_id}`}
										collapsingIndexes={[0, 2]}
										centeredIndexes={[2]}
										onClick={onPriceSelected(a)}
										data={[
											a.price_id,
											a.price_name,
											<Money value={a.cost+a.commission} />
										]} 
									/>
								))}
							</Table.Body>
						</Table>
					)}
				</> : (
					null
				)}
			</Modal.Content>
			<Modal.Actions>
				<Button text={t('common.cancel')} onClick={bindClick(setSelectModal)} />
			</Modal.Actions>
		</Modal>
	</>
}

export default UpdateSeatTicket
